import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { SelectInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import { setStatusSichtbarkeitCache } from "@/hooks/useStatusSichtbarkeit";
import {
  DOMAIN_STATUS_KEYS,
  STATUS_SICHTBARKEIT_DOMAIN_LABELS,
  STATUS_SICHTBARKEIT_DOMAINS,
  STATUS_SICHTBARKEIT_LEVEL_LABELS,
  defaultStatusSichtbarkeit,
  getStatusSichtbarkeit,
  normalizeStatusSichtbarkeitLevel,
  saveStatusSichtbarkeit,
  statusLabelForDomain,
  type StatusSichtbarkeitConfig,
  type StatusSichtbarkeitDomain,
  type StatusSichtbarkeitLevel,
} from "@/lib/statusSichtbarkeit";
import { toast } from "@/lib/toast";
import { SettingsAccessDenied } from "./SettingsLayout";
import { useSettingsAcl } from "./lib/useSettingsAcl";

export function StatusSichtbarkeitPage() {
  const acl = useSettingsAcl();
  const [config, setConfig] = useState<StatusSichtbarkeitConfig>(defaultStatusSichtbarkeit);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setConfig(await getStatusSichtbarkeit());
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Status-Sichtbarkeit konnte nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!acl.loading && !acl.canAuftraege) {
    return <SettingsAccessDenied />;
  }

  function setLevel(
    domain: StatusSichtbarkeitDomain,
    status: string,
    level: StatusSichtbarkeitLevel,
  ) {
    setConfig((prev) => ({
      ...prev,
      [domain]: {
        ...prev[domain],
        [status]: level,
      },
    }));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const saved = await saveStatusSichtbarkeit(config);
      setStatusSichtbarkeitCache(saved);
      setConfig(saved);
      toast.success("Status-Sichtbarkeit gespeichert.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card
        title="Status-Sichtbarkeit"
        description="Pro Status festlegen, wer ihn sehen darf. Nutzer ohne Büro-/Admin-Recht sehen bei verdeckten Status den letzten Status in der Workflow-Reihenfolge, den sie sehen dürfen. Einträge bleiben sichtbar."
        actions={
          <Button type="button" onClick={() => void handleSave()} disabled={saving || loading}>
            Speichern
          </Button>
        }
      >
        {error ? <Alert variant="error">{error}</Alert> : null}
        {loading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-label-secondary">
            <Spinner />
            Einstellungen werden geladen …
          </div>
        ) : (
          <div className="space-y-6">
            {STATUS_SICHTBARKEIT_DOMAINS.map((domain) => (
              <section key={domain} className="space-y-3">
                <h3 className="text-sm font-semibold text-label">
                  {STATUS_SICHTBARKEIT_DOMAIN_LABELS[domain]}
                </h3>
                <div className="space-y-2">
                  {DOMAIN_STATUS_KEYS[domain].map((status) => (
                    <div
                      key={`${domain}-${status}`}
                      className="grid gap-3 rounded-lg border border-separator bg-card-elevated p-3 sm:grid-cols-[1fr_180px]"
                    >
                      <div className="flex flex-col justify-center">
                        <span className="text-sm font-medium text-label">
                          {statusLabelForDomain(domain, status)}
                        </span>
                        <span className="text-xs text-label-tertiary">{status}</span>
                      </div>
                      <SelectInput
                        label="Sichtbarkeit"
                        value={normalizeStatusSichtbarkeitLevel(config[domain][status])}
                        onChange={(e) =>
                          setLevel(
                            domain,
                            status,
                            e.target.value as StatusSichtbarkeitLevel,
                          )
                        }
                      >
                        {(
                          Object.entries(STATUS_SICHTBARKEIT_LEVEL_LABELS) as [
                            StatusSichtbarkeitLevel,
                            string,
                          ][]
                        ).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </SelectInput>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
